/**
 * Hero Slideshow Section Component
 * Fade-effect Swiper hero, rebuilt from the live malbon.com homepage slideshow.
 */

// Inputs that also finalize LCP; slides 2+ load and autoplay starts on them, so they never compete with or replace the LCP
const FIRST_INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'];
// Without any input, start anyway this long after window load (matches the SSO loader's 6s)
const AUTOPLAY_FALLBACK_MS = 6000;

export class HeroSlideshow extends HTMLElement {
  swiper = null;
  onSwiperLoad = null;
  userPaused = false;

  constructor() {
    super();
    this.onFirstInteraction = this.onFirstInteraction.bind(this);
    this.onWindowLoad = this.onWindowLoad.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
  }

  connectedCallback() {
    if (typeof Swiper !== 'undefined') {
      this.init();
      return;
    }

    const swiperScript = document.getElementById('swiper-script');
    if (!swiperScript) return;

    this.onSwiperLoad = this.init.bind(this);
    swiperScript.addEventListener('load', this.onSwiperLoad);
  }

  disconnectedCallback() {
    if (this.onSwiperLoad) {
      document.getElementById('swiper-script')?.removeEventListener('load', this.onSwiperLoad);
      this.onSwiperLoad = null;
    }

    this.removeFirstInteractionListeners();
    this.autoplayToggle?.removeEventListener('click', this.onToggleClick);
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  init() {
    const swiperEl = this.querySelector('.hero-slideshow__slider');
    if (!swiperEl || swiperEl.classList.contains('swiper-initialized')) return;

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    const hasMultipleSlides = slides.length > 1;

    const config = {
      slidesPerView: 1,
      loop: hasMultipleSlides,
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      speed: 200,
      watchOverflow: true,
      // Static slide markup — MutationObservers only add update churn on resize
      observer: false,
      observeParents: false,
      observeSlideChildren: false,
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = this.getAttribute('data-autoplay') === 'true';

    if (autoplayEnabled && hasMultipleSlides && !prefersReducedMotion) {
      const autoplayDelay = (parseInt(this.getAttribute('data-autoplay-delay'), 10) || 5) * 1000;

      config.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      };
    }

    const prevButton = this.querySelector('.hero-slideshow__nav--prev');
    const nextButton = this.querySelector('.hero-slideshow__nav--next');
    if (prevButton && nextButton) {
      config.navigation = {
        prevEl: prevButton,
        nextEl: nextButton,
      };
    }

    const paginationEl = this.querySelector('.hero-slideshow__pagination');
    if (paginationEl) {
      config.pagination = {
        el: paginationEl,
        clickable: true,
      };
    }

    try {
      this.swiper = new Swiper(swiperEl, config);
    } catch (error) {
      console.error('Error initializing Hero Slideshow Swiper:', error);
      return;
    }

    if (!hasMultipleSlides) return;

    // Pause/play control (WCAG 2.2.2), only when autoplay is actually configured (not under reduced motion)
    this.autoplayToggle = this.querySelector('.hero-slideshow__autoplay-toggle');
    if (this.autoplayToggle && config.autoplay) {
      this.autoplayToggle.hidden = false;
      this.autoplayToggle.addEventListener('click', this.onToggleClick);
    }

    this.swiper.autoplay?.stop();
    for (const type of FIRST_INTERACTION_EVENTS) {
      window.addEventListener(type, this.onFirstInteraction, { passive: true });
    }

    if (document.readyState === 'complete') {
      this.onWindowLoad();
    } else {
      window.addEventListener('load', this.onWindowLoad);
    }
  }

  onWindowLoad() {
    window.removeEventListener('load', this.onWindowLoad);
    this.fallbackTimer = setTimeout(this.onFirstInteraction, AUTOPLAY_FALLBACK_MS);
  }

  onFirstInteraction() {
    this.removeFirstInteractionListeners();
    this.loadSlideImages();
    if (this.swiper?.params.autoplay?.enabled && !this.userPaused) this.swiper.autoplay.start();
  }

  onToggleClick() {
    this.userPaused = !this.userPaused;
    if (this.userPaused) {
      this.swiper?.autoplay.stop();
    } else {
      this.loadSlideImages();
      this.swiper?.autoplay.start();
    }

    const toggle = this.autoplayToggle;
    toggle.classList.toggle('is-paused', this.userPaused);
    toggle.setAttribute('aria-label', this.userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
  }

  /* Slides 2+ render with data-src/data-srcset (see hero-slideshow.liquid); swap them in on first interaction. */
  loadSlideImages() {
    for (const el of this.querySelectorAll('[data-srcset], [data-src]')) {
      if (el.dataset.srcset) el.srcset = el.dataset.srcset;
      if (el.dataset.src) el.src = el.dataset.src;
      el.removeAttribute('data-srcset');
      el.removeAttribute('data-src');
    }
  }

  removeFirstInteractionListeners() {
    for (const type of FIRST_INTERACTION_EVENTS) {
      window.removeEventListener(type, this.onFirstInteraction);
    }
    window.removeEventListener('load', this.onWindowLoad);
    clearTimeout(this.fallbackTimer);
  }
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
